import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppContextProvider } from '@contexts/AppContext';
import useBlueconicEvent from '@hooks/useBlueconicEvent';
import useEventTracking from '@hooks/useEventTracking';
import { TARGET_APP_VERSION, getNativeMajorVersion } from '@hooks/useNativeAppVersionCheck';
import { Page } from '@tracking';

import ForYouContent from './Content';
import type { ForYouProps } from './interfaces';

const queryClient = new QueryClient();

// In a webview, MarketingModal (the usual host of useBlueconicEvent) is suppressed,
// so the BlueConic dialogue subscription must be mounted here for For You.
const BlueconicEventBridge = () => {
  useBlueconicEvent();
  return null;
};

const ForYou = ({
  bookmarks,
  env,
  headlineVariant,
  initialCurrentUser,
  isGdprRequired,
  isNativeApp,
  isWebview,
  nativeAppVersion,
  newsletterNames,
}: ForYouProps) => {
  const { Track } = useEventTracking({
    page: Page.ForYou,
  });
  const isValidAppVersion = getNativeMajorVersion(nativeAppVersion) >= TARGET_APP_VERSION;

  return (
    <AppContextProvider
      env={env}
      initialCurrentUser={initialCurrentUser}
      isGdprRequired={isGdprRequired}
      isNativeApp={isNativeApp}
      isWebview={isWebview}
      nativeAppVersion={nativeAppVersion}
    >
      <QueryClientProvider client={queryClient}>
        <Track>
          {isNativeApp && isValidAppVersion && <BlueconicEventBridge />}
          <ForYouContent
            bookmarks={bookmarks}
            headlineVariant={headlineVariant}
            initialCurrentUser={initialCurrentUser}
            newsletterNames={newsletterNames}
          />
        </Track>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default ForYou;
